// Løn til steamerne (Freemi 0.52.0, design 5a–5e). Ren beregning uden UI – samme regler som pixie_loen_beregn i
// supabase/migrations/20261107000000_loen.sql (tests/db/loen.test.ts sammenligner dem på de samme data).
//   • Lønform: pr. opgave (standard), timeløn eller fast løn pr. måned. Lønperioden er måneden (1.–sidste).
//   • Pr. opgave: sats pr. færdig opgave – firmasats for firmakunder (GoMore m.fl.). Ny omgang (reklamation) og aflyste giver
//     ikke løn. "Kunden var ikke hjemme" betales som en opgave (kan slås fra i lønmodellen).
//   • Provision af tilvalg (bestilt) + godkendt mersalg på færdige opgaver, ekskl. moms.
//   • Bonus pr. færdig totalklargøring + månedsbonus ved mindst N femstjernede anmeldelser fra egne kunder og 0 klager i perioden
//     (kundesager om fejl/skade, der ikke er afvist, og ny omgang på steamerens opgaver). Gælder alle lønformer.
//   • Satserne, der gælder fra periodens første dag, bruges for hele perioden. Feriepenge og pension (tillæg) indgår kun i Analyse.
// Alle beløb er heltal i øre.
import type { Booking } from './typer.ts';

export type Lonform = 'opg' | 'time' | 'fast';
export const LONFORMER: [Lonform, string][] = [['opg', 'Pr. opgave'], ['time', 'Timeløn'], ['fast', 'Fast løn']];
export const lonformNavn = (f: Lonform) => LONFORMER.find((x) => x[0] === f)?.[1] ?? f;

/** Freemis standard, indtil virksomheden har sat sin lønmodel (som i designet). */
export const LOEN_STANDARD = {
  lonform: 'opg' as Lonform, opg_sats: 35000, firma_sats: 30000, provision_pct: 30, total_bonus: 25000, maaned_bonus: 50000, min_fem: 3,
  ikke_hjemme: true, tillaeg_pct: 12.5,
};

export type LoenModel = {
  gaelder_fra: string; lonform: Lonform; opg_sats: number; firma_sats: number; provision_pct: number; total_bonus: number; maaned_bonus: number;
  min_fem: number; total_pakker: string[]; ikke_hjemme: boolean; tillaeg_pct: number;
};
/** Steamerens egne satser fra en måned. null = lønmodellens. */
export type LoenSteamerSats = {
  steamer_id: string; gaelder_fra: string; lonform: Lonform | null; opg_sats: number | null; firma_sats: number | null; provision_pct: number | null;
  timeloen: number | null; fast_maaned: number | null;
};
export type LoenFirma = { id?: string; navn: string; partner_id: string | null; kunde_id: string | null };
export type LoenSatser = Omit<LoenModel, 'gaelder_fra'> & { antaget: boolean; gaelder_fra: string | null; timeloen: number; fast_maaned: number };
export type LoenOpsaetning = { standard: LoenSatser; modeller: LoenModel[]; steamere: LoenSteamerSats[]; firma: LoenFirma[]; laast_til: string | null };

export type LoenTal = {
  fra: string; til: string; satser: LoenSatser; lonform: Lonform;
  opgaver: { antal: number; sats: number; beloeb: number };
  firma: { antal: number; sats: number; beloeb: number };
  timer: { minutter: number; sats: number; beloeb: number };
  fast: { beloeb: number };
  provision: { grundlag: number; pct: number; beloeb: number };
  total: { antal: number; sats: number; beloeb: number };
  maaned: { fem: number; min: number; klager: number; sats: number; beloeb: number };
  ialt: number;
};
/** En opgave i lønnen – uden kundens navn. */
export type LoenOpgave = { id: number; dato: string; ydelse: string; postnr: string; by: string; status: 'faerdig' | 'ikke_hjemme'; firma: boolean; grundlag: number; total: boolean };
/** En anmeldelse fra steamerens kunde – stjerner og kommentar, aldrig navnet. */
export type LoenAnmeldelse = { id: number; besvaret: string; stjerner: number; kommentar: string; opgave_dato: string; postnr: string; by: string; i_perioden: boolean };
export type LoenPeriode = {
  steamer: { id: string; navn: string }; fra: string; til: string; idag: string; tal: LoenTal;
  godkendt: { tid: string; af: string } | null; opgaver: LoenOpgave[]; anmeldelser: LoenAnmeldelse[];
};
export type LoenOversigt = { steamer_id: string; navn: string; ialt: number; lonform: Lonform; godkendt: boolean };
/** Lønnen pr. steamer i en Analyse-periode (ekskl. tillæg), lagt sammen måned for måned. */
export type LoenAnalyse = {
  steamer_id: string; lonform: Lonform;
  opgAntal: number; opg: number; firmaAntal: number; firma: number; minutter: number; timer: number; fast: number;
  provision: number; total: number; maaned: number;
  /** måneder med månedsbonus / måneder i alt */ maanedFik: number; maaneder: number;
  /** lønnen, hvis alle opgaver var betalt pr. opgave (til sammenligning ved timeløn og fast løn) */ prOpgave: number;
  /** feriepenge og pension i procent */ tillaeg: number; antaget: boolean;
};
export const analyseGrundloen = (l: LoenAnalyse) => l.opg + l.firma + l.timer + l.fast;
export const analyseBonus = (l: LoenAnalyse) => l.total + l.maaned;
/** Lønnen ud fra opgaverne alene (til at lægge ud på opgaverne i Analyse). */
export function loenAnalyseFraTal(steamer_id: string, l: LoenTal[]): LoenAnalyse {
  const s = (f: (t: LoenTal) => number) => l.reduce((x, t) => x + f(t), 0);
  return {
    steamer_id, lonform: l.at(-1)?.lonform ?? 'opg',
    opgAntal: s((t) => t.opgaver.antal), opg: s((t) => t.opgaver.beloeb), firmaAntal: s((t) => t.firma.antal), firma: s((t) => t.firma.beloeb),
    minutter: s((t) => t.timer.minutter), timer: s((t) => t.timer.beloeb), fast: s((t) => t.fast.beloeb), provision: s((t) => t.provision.beloeb),
    total: s((t) => t.total.beloeb), maaned: s((t) => t.maaned.beloeb), maanedFik: l.filter((t) => t.maaned.beloeb > 0).length, maaneder: l.length,
    prOpgave: s((t) => t.opgaver.antal * t.opgaver.sats + t.firma.antal * t.firma.sats),
    tillaeg: Math.max(0, ...l.map((t) => t.satser.tillaeg_pct)), antaget: l.some((t) => t.satser.antaget),
  };
}

// ---------- Datoer ----------
export const maanedStart = (dato: string) => dato.slice(0, 7) + '-01';
export const maanedSlut = (fra: string) => { const [y, m] = fra.split('-').map(Number); const d = new Date(Date.UTC(y, m, 0)); return d.toISOString().slice(0, 10); };
export const dageIMaaned = (fra: string) => Number(maanedSlut(fra).slice(8, 10));
export const forrigeMaaned = (fra: string, n = 1) => { const [y, m] = fra.split('-').map(Number); const t = y * 12 + m - 1 - n; return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, '0')}-01`; };
const MDR = ['januar', 'februar', 'marts', 'april', 'maj', 'juni', 'juli', 'august', 'september', 'oktober', 'november', 'december'];
export const maanedNavn = (fra: string, aar = false) => MDR[Number(fra.slice(5, 7)) - 1] + (aar ? ' ' + fra.slice(0, 4) : '');
/** Dansk dato for et tidspunkt (anmeldelser og klager tæller i den periode, de er givet i). */
const DANSK = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Copenhagen' });
export const danskDato = (tid: string) => (/^\d{4}-\d\d-\d\d$/.test(tid) ? tid : DANSK.format(new Date(tid)));

// ---------- Satser ----------
/** De satser, der gælder for steameren på datoen (seneste "gælder fra" ≤ dato): steamerens egne – ellers lønmodellens –
 *  ellers standarden (antaget). Uden model er totalklargøring de pakker, der hedder noget med "total" og "klarg". */
export function loenSatser(o: { modeller: LoenModel[]; steamere: LoenSteamerSats[] }, steamer_id: string | null, dato: string, ydelser: { id: string; navn: string }[] = []): LoenSatser {
  const m = o.modeller.filter((x) => x.gaelder_fra <= dato).sort((a, b) => b.gaelder_fra.localeCompare(a.gaelder_fra))[0];
  const s = steamer_id ? o.steamere.filter((x) => x.steamer_id === steamer_id && x.gaelder_fra <= dato).sort((a, b) => b.gaelder_fra.localeCompare(a.gaelder_fra))[0] : undefined;
  const fra = [m?.gaelder_fra, s?.gaelder_fra].filter((x): x is string => !!x).sort().at(-1) ?? null;
  return {
    antaget: !m, gaelder_fra: fra,
    lonform: s?.lonform ?? m?.lonform ?? LOEN_STANDARD.lonform,
    opg_sats: s?.opg_sats ?? m?.opg_sats ?? LOEN_STANDARD.opg_sats,
    firma_sats: s?.firma_sats ?? m?.firma_sats ?? LOEN_STANDARD.firma_sats,
    provision_pct: s?.provision_pct ?? m?.provision_pct ?? LOEN_STANDARD.provision_pct,
    timeloen: s?.timeloen ?? 0, fast_maaned: s?.fast_maaned ?? 0,
    total_bonus: m?.total_bonus ?? LOEN_STANDARD.total_bonus, maaned_bonus: m?.maaned_bonus ?? LOEN_STANDARD.maaned_bonus, min_fem: m?.min_fem ?? LOEN_STANDARD.min_fem,
    total_pakker: m ? m.total_pakker : ydelser.filter((y) => /total/i.test(y.navn) && /klarg/i.test(y.navn)).map((y) => y.id).sort(),
    ikke_hjemme: m?.ikke_hjemme ?? LOEN_STANDARD.ikke_hjemme, tillaeg_pct: m?.tillaeg_pct ?? LOEN_STANDARD.tillaeg_pct,
  };
}

/** Er bookingen for en firmakunde med egen sats? (partneraftale, kunde eller partnernavnet, fx "GoMore") */
export function erFirma(firma: LoenFirma[], b: Pick<Booking, 'partner_id' | 'kunde_id' | 'partner' | 'kilde'>): boolean {
  const n = (x: string | null | undefined) => (x ?? '').trim().toLowerCase();
  return firma.some((f) => (f.partner_id != null && f.partner_id === b.partner_id) || (f.kunde_id != null && f.kunde_id === b.kunde_id)
    || (f.partner_id == null && f.kunde_id == null && (n(b.partner) === n(f.navn) || (b.kilde === 'gomore' && n(f.navn) === 'gomore'))));
}

/** Grundlaget for provisionen: tilvalg, der blev bestilt, + godkendt mersalg, ekskl. moms (en nedjustering tæller ikke). */
export const provisionGrundlag = (b: Pick<Booking, 'tilvalg' | 'ekstra'>) =>
  (b.tilvalg ?? []).filter((t) => !t.ekstra_id).reduce((s, t) => s + (t.pris ?? 0), 0)
  + (b.ekstra ?? []).filter((e) => e.status === 'godkendt' && !(e.type === 'aendring' && e.beloeb < 0)).reduce((s, e) => s + e.beloeb, 0);

/** Opgaver, der giver løn: færdige – og "kunden var ikke hjemme", når det betales. Aldrig ny omgang. */
export const betaltOpgave = (b: Pick<Booking, 'status' | 'felter' | 'reklamation_for'>, ikkeHjemme: boolean) =>
  b.reklamation_for == null && (b.status === 'faerdig' || (ikkeHjemme && b.status === 'aflyst' && b.felter?.udeblev === 'ja'));

export type LoenKlage = { type: string; status: string; afgoerelse?: string | null; steamer_id: string | null; booking_id: number | null; oprettet: string };
export type LoenInput = {
  satser: LoenSatser; steamer_id: string; fra: string; til: string;
  /** Alle bookinger, der kan være relevante (steamerens i perioden + reklamationer på dem). */
  bookinger: Booking[];
  anmeldelser: { steamer_id: string | null; stjerner: number | null; besvaret: string | null }[];
  klager: LoenKlage[];
  firma: LoenFirma[];
  /** Vagttimerne i perioden (kun til timeløn), i minutter. */
  minutter: number;
};

/** Steamerens løn for fra–til (inden for én måned). */
export function beregnLoen(a: LoenInput): LoenTal {
  const s = a.satser, form = s.lonform;
  const mine = a.bookinger.filter((b) => b.steamer_id === a.steamer_id && !!b.dato && b.dato >= a.fra && b.dato <= a.til && betaltOpgave(b, s.ikke_hjemme));
  const firma = mine.filter((b) => erFirma(a.firma, b));
  const faerdige = mine.filter((b) => b.status === 'faerdig');
  const grundlag = faerdige.reduce((x, b) => x + provisionGrundlag(b), 0);
  const nTotal = faerdige.filter((b) => s.total_pakker.includes(b.ydelse_id)).length;
  const iPerioden = (tid: string | null) => !!tid && danskDato(tid) >= a.fra && danskDato(tid) <= a.til;
  const fem = a.anmeldelser.filter((x) => x.steamer_id === a.steamer_id && x.stjerner === 5 && iPerioden(x.besvaret)).length;
  const paaId = new Map(a.bookinger.map((b) => [b.id, b]));
  const klager = a.klager.filter((k) => (k.steamer_id ?? (k.booking_id != null ? paaId.get(k.booking_id)?.steamer_id : null)) === a.steamer_id
      && (k.type === 'fejl' || k.type === 'skade') && k.status !== 'afvist' && k.afgoerelse !== 'afvis' && iPerioden(k.oprettet)).length
    + a.bookinger.filter((r) => r.reklamation_for != null && r.status !== 'aflyst' && !!r.dato && r.dato >= a.fra && r.dato <= a.til
      && paaId.get(r.reklamation_for)?.steamer_id === a.steamer_id).length;
  const nOpg = mine.length - firma.length;
  const opg = form === 'opg' ? nOpg * s.opg_sats : 0;
  const fb = form === 'opg' ? firma.length * s.firma_sats : 0;
  const tim = form === 'time' ? Math.round(a.minutter * s.timeloen / 60) : 0;
  const dage = Math.round((Date.parse(a.til) - Date.parse(a.fra)) / 864e5) + 1;
  const fast = form === 'fast' ? Math.round(s.fast_maaned * dage / dageIMaaned(a.fra)) : 0;
  const prov = Math.round(grundlag * s.provision_pct / 100);
  const tb = nTotal * s.total_bonus;
  const mb = fem >= s.min_fem && klager === 0 ? s.maaned_bonus : 0;
  return {
    fra: a.fra, til: a.til, satser: s, lonform: form,
    opgaver: { antal: nOpg, sats: s.opg_sats, beloeb: opg }, firma: { antal: firma.length, sats: s.firma_sats, beloeb: fb },
    timer: { minutter: a.minutter, sats: s.timeloen, beloeb: tim }, fast: { beloeb: fast },
    provision: { grundlag, pct: s.provision_pct, beloeb: prov }, total: { antal: nTotal, sats: s.total_bonus, beloeb: tb },
    maaned: { fem, min: s.min_fem, klager, sats: s.maaned_bonus, beloeb: mb },
    ialt: opg + fb + tim + fast + prov + tb + mb,
  };
}
/** Grundløn (opgaver, firma, timer eller fast) – uden provision og bonus. */
export const grundloen = (t: LoenTal) => t.opgaver.beloeb + t.firma.beloeb + t.timer.beloeb + t.fast.beloeb;
export const bonus = (t: LoenTal) => t.total.beloeb + t.maaned.beloeb;
/** Feriepenge og pension oven i (kun til Analyse). */
export const tillaeg = (t: LoenTal) => Math.round(t.ialt * t.satser.tillaeg_pct / 100);

// ---------- Til lønsystemet (CSV) ----------
export type Loenart = { art: string; antal: number; sats: number; beloeb: number };
/** Lønarterne for en periode (kun linjer med et beløb) – som i designet 5c. */
export function loenarter(t: LoenTal): Loenart[] {
  const l: Loenart[] = [
    { art: 'Opgaver', antal: t.opgaver.antal, sats: t.opgaver.sats, beloeb: t.opgaver.beloeb },
    { art: 'Opgaver – firmakunder', antal: t.firma.antal, sats: t.firma.sats, beloeb: t.firma.beloeb },
    { art: 'Timeløn', antal: Math.round(t.timer.minutter / 6) / 10, sats: t.timer.sats, beloeb: t.timer.beloeb },
    { art: 'Fast løn', antal: 1, sats: t.fast.beloeb, beloeb: t.fast.beloeb },
    { art: `Provision ${fmtPct(t.provision.pct)} %`, antal: 1, sats: t.provision.beloeb, beloeb: t.provision.beloeb },
    { art: 'Bonus – totalklargøring', antal: t.total.antal, sats: t.total.sats, beloeb: t.total.beloeb },
    { art: 'Bonus – måned', antal: 1, sats: t.maaned.beloeb, beloeb: t.maaned.beloeb },
  ];
  return l.filter((x) => x.beloeb !== 0);
}
const fmtPct = (x: number) => String(Math.round(x * 100) / 100).replace('.', ',');
/** Beløb i kroner med komma (til CSV): 181080 → "1810,80". */
export const kroner = (oere: number) => (oere < 0 ? '-' : '') + Math.floor(Math.abs(oere) / 100) + ',' + String(Math.abs(oere) % 100).padStart(2, '0');
export const CSV_KOLONNER = ['Medarbejder', 'Periode', 'Lønart', 'Antal', 'Sats', 'Beløb'];
/** Rækkerne til lønsystemet (semikolon-CSV laves af appens tilCsv). */
export function loenCsvRaekker(l: { navn: string; tal: LoenTal }[]): string[][] {
  return l.flatMap(({ navn, tal }) => loenarter(tal).map((x) => [navn, `${tal.fra}–${tal.til}`, x.art, String(x.antal).replace('.', ','), kroner(x.sats), kroner(x.beloeb)]));
}

// ---------- Tjek af opsætningen (samme grænser som databasen) ----------
const heltal = (x: unknown, maks: number) => typeof x === 'number' && Number.isInteger(x) && x >= 0 && x <= maks;
const pct = (x: unknown) => typeof x === 'number' && x >= 0 && x <= 100;
export function tjekLoenModel(m: LoenModel): Record<string, string> {
  const f: Record<string, string> = {};
  if (!/^\d{4}-\d\d-01$/.test(m.gaelder_fra)) f.gaelder_fra = 'Satserne gælder fra den 1. i en måned.';
  if (!heltal(m.opg_sats, 1000000)) f.opg_sats = 'Skal være mellem 0 og 10.000 kr.';
  if (!heltal(m.firma_sats, 1000000)) f.firma_sats = 'Skal være mellem 0 og 10.000 kr.';
  if (!pct(m.provision_pct)) f.provision_pct = 'Skal være mellem 0 og 100 %.';
  if (!heltal(m.total_bonus, 1000000)) f.total_bonus = 'Skal være mellem 0 og 10.000 kr.';
  if (!heltal(m.maaned_bonus, 10000000)) f.maaned_bonus = 'Skal være mellem 0 og 100.000 kr.';
  if (!heltal(m.min_fem, 100)) f.min_fem = 'Skal være mellem 0 og 100.';
  if (!pct(m.tillaeg_pct)) f.tillaeg_pct = 'Skal være mellem 0 og 100 %.';
  return f;
}
export function tjekLoenSteamer(s: LoenSteamerSats): string | null {
  if (!/^\d{4}-\d\d-01$/.test(s.gaelder_fra)) return 'Satserne gælder fra den 1. i en måned.';
  if (s.opg_sats != null && !heltal(s.opg_sats, 1000000)) return 'Satsen pr. opgave skal være mellem 0 og 10.000 kr.';
  if (s.firma_sats != null && !heltal(s.firma_sats, 1000000)) return 'Firmasatsen skal være mellem 0 og 10.000 kr.';
  if (s.provision_pct != null && !pct(s.provision_pct)) return 'Provisionen skal være mellem 0 og 100 %.';
  if (s.timeloen != null && !heltal(s.timeloen, 200000)) return 'Timelønnen skal være mellem 0 og 2.000 kr.';
  if (s.fast_maaned != null && !heltal(s.fast_maaned, 20000000)) return 'Den faste løn skal være mellem 0 og 200.000 kr. pr. måned.';
  if (s.lonform === 'time' && s.timeloen == null) return 'Skriv timelønnen.';
  if (s.lonform === 'fast' && s.fast_maaned == null) return 'Skriv den faste løn pr. måned.';
  return null;
}

// ---------- Fra databasen ----------
const tal = (x: unknown) => (x == null ? 0 : Number(x));
const talN = (x: unknown) => (x == null ? null : Number(x));
const dato = (x: unknown) => String(x ?? '').slice(0, 10);
type R = Record<string, unknown>;
export function satserFraDb(x: R): LoenSatser {
  return {
    antaget: !!x.antaget, gaelder_fra: x.gaelder_fra ? dato(x.gaelder_fra) : null, lonform: (x.lonform as Lonform) ?? 'opg',
    opg_sats: tal(x.opg_sats), firma_sats: tal(x.firma_sats), provision_pct: tal(x.provision_pct), timeloen: tal(x.timeloen), fast_maaned: tal(x.fast_maaned),
    total_bonus: tal(x.total_bonus), maaned_bonus: tal(x.maaned_bonus), min_fem: tal(x.min_fem), total_pakker: ((x.total_pakker as string[]) ?? []).slice().sort(),
    ikke_hjemme: x.ikke_hjemme !== false, tillaeg_pct: tal(x.tillaeg_pct),
  };
}
export function talFraDb(x: R): LoenTal {
  const d = (k: string) => (x[k] ?? {}) as R;
  return {
    fra: dato(x.fra), til: dato(x.til), satser: satserFraDb(d('satser')), lonform: (x.lonform as Lonform) ?? 'opg',
    opgaver: { antal: tal(d('opgaver').antal), sats: tal(d('opgaver').sats), beloeb: tal(d('opgaver').beloeb) },
    firma: { antal: tal(d('firma').antal), sats: tal(d('firma').sats), beloeb: tal(d('firma').beloeb) },
    timer: { minutter: tal(d('timer').minutter), sats: tal(d('timer').sats), beloeb: tal(d('timer').beloeb) },
    fast: { beloeb: tal(d('fast').beloeb) },
    provision: { grundlag: tal(d('provision').grundlag), pct: tal(d('provision').pct), beloeb: tal(d('provision').beloeb) },
    total: { antal: tal(d('total').antal), sats: tal(d('total').sats), beloeb: tal(d('total').beloeb) },
    maaned: { fem: tal(d('maaned').fem), min: tal(d('maaned').min), klager: tal(d('maaned').klager), sats: tal(d('maaned').sats), beloeb: tal(d('maaned').beloeb) },
    ialt: tal(x.ialt),
  };
}
export function periodeFraDb(x: R): LoenPeriode {
  const g = x.godkendt as R | null;
  return {
    steamer: x.steamer as { id: string; navn: string }, fra: dato(x.fra), til: dato(x.til), idag: dato(x.idag), tal: talFraDb(x.tal as R),
    godkendt: g ? { tid: String(g.tid), af: String(g.af) } : null,
    opgaver: ((x.opgaver as R[]) ?? []).map((o) => ({ id: Number(o.id), dato: dato(o.dato), ydelse: String(o.ydelse ?? ''), postnr: String(o.postnr ?? ''), by: String(o.by ?? ''),
      status: o.status === 'faerdig' ? 'faerdig' : 'ikke_hjemme', firma: !!o.firma, grundlag: tal(o.grundlag), total: !!o.total })),
    anmeldelser: ((x.anmeldelser as R[]) ?? []).map((a) => ({ id: Number(a.id), besvaret: String(a.besvaret), stjerner: tal(a.stjerner), kommentar: String(a.kommentar ?? ''),
      opgave_dato: dato(a.opgave_dato), postnr: String(a.postnr ?? ''), by: String(a.by ?? ''), i_perioden: !!a.i_perioden })),
  };
}
export function opsaetningFraDb(x: R): LoenOpsaetning {
  return {
    standard: satserFraDb((x.standard ?? {}) as R),
    modeller: ((x.modeller as R[]) ?? []).map((m) => ({ gaelder_fra: dato(m.gaelder_fra), lonform: m.lonform as Lonform, opg_sats: tal(m.opg_sats), firma_sats: tal(m.firma_sats),
      provision_pct: tal(m.provision_pct), total_bonus: tal(m.total_bonus), maaned_bonus: tal(m.maaned_bonus), min_fem: tal(m.min_fem), total_pakker: (m.total_pakker as string[]) ?? [],
      ikke_hjemme: m.ikke_hjemme !== false, tillaeg_pct: tal(m.tillaeg_pct) })),
    steamere: ((x.steamere as R[]) ?? []).map((s) => ({ steamer_id: String(s.steamer_id), gaelder_fra: dato(s.gaelder_fra), lonform: (s.lonform as Lonform | null) ?? null,
      opg_sats: talN(s.opg_sats), firma_sats: talN(s.firma_sats), provision_pct: talN(s.provision_pct), timeloen: talN(s.timeloen), fast_maaned: talN(s.fast_maaned) })),
    firma: ((x.firma as R[]) ?? []).map((f) => ({ id: String(f.id), navn: String(f.navn), partner_id: (f.partner_id as string | null) ?? null, kunde_id: (f.kunde_id as string | null) ?? null })),
    laast_til: x.laast_til ? dato(x.laast_til) : null,
  };
}
export const oversigtFraDb = (x: unknown): LoenOversigt[] => ((x as R[]) ?? []).map((r) => ({ steamer_id: String(r.steamer_id), navn: String(r.navn), ialt: tal(r.ialt),
  lonform: (r.lonform as Lonform) ?? 'opg', godkendt: !!r.godkendt }));
export const analyseLoenFraDb = (x: unknown): LoenAnalyse[] => ((x as R[]) ?? []).map((r) => ({ steamer_id: String(r.steamer_id), lonform: (r.lonform as Lonform) ?? 'opg',
  opgAntal: tal(r.opg_antal), opg: tal(r.opg), firmaAntal: tal(r.firma_antal), firma: tal(r.firma), minutter: tal(r.minutter), timer: tal(r.timer), fast: tal(r.fast),
  provision: tal(r.provision), total: tal(r.total), maaned: tal(r.maaned), maanedFik: tal(r.maaned_fik), maaneder: tal(r.maaneder), prOpgave: tal(r.pr_opgave),
  tillaeg: tal(r.tillaeg), antaget: !!r.antaget }));
